import { inject, Injectable, Injector, Type } from '@angular/core';
import {
  DefaultFieldsProvider,
  DefaultFormSettingsProvider,
} from '../models/default-form-fields.model';
import { CarriersListService } from './carriers-list.service';
import { CommitsService } from './commits.service';
import { FinancialModuleService } from './financial-module.service';
import { PartNumbersListService } from './partnumbers-list.service';
import { PortfolioService } from './portfolio.service';
import { ShipmentsService } from './shipments.service';
import { SlotsService } from './slots.service';
import { StocksListService } from './stocks-list.service';
import { SupplyVisibilityService } from './supply-visibility.service';
import { VendorsListService } from './vendors-list.service';

/**
 * Single place that maps a module key to the services backing its default
 * fields.
 */
@Injectable({ providedIn: 'root' })
export class DefaultFormFieldsRegistry {
  private readonly injector = inject(Injector);

  /** Modules whose settings matrix the admin dialog can edit. */
  private readonly settingsProviders: Record<string, Type<DefaultFormSettingsProvider>> = {
    'carriers-module': CarriersListService,
    'commits-module': CommitsService,
    'financial-module': FinancialModuleService,
    'partnumbers-module': PartNumbersListService,
    'portfolio-module': PortfolioService,
    'shipments-module': ShipmentsService,
    'slots-module': SlotsService,
    'stocks-module': StocksListService,
    'vendors-module': VendorsListService,
  };

  private readonly fieldsProviders: Record<string, Type<DefaultFieldsProvider>> = {
    ...this.settingsProviders,
    'commits-module': SupplyVisibilityService,
  };

  /** `undefined` for a module that has no editable settings matrix. */
  settingsProvider(module: string): DefaultFormSettingsProvider | undefined {
    const type = this.settingsProviders[module];
    return type ? this.injector.get(type) : undefined;
  }

  /** `undefined` for a module that exposes no resolved field list. */
  fieldsProvider(module: string): DefaultFieldsProvider | undefined {
    const type = this.fieldsProviders[module];
    return type ? this.injector.get(type) : undefined;
  }
}
